import { AfterViewInit, ChangeDetectorRef, Component, Inject, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RseSignService } from '../../services/rse-sign.service';
import { SignRseProcessResponse } from '../../models/sign-rse-api.model';
import { modalPanelAnimation } from '../../core/animations/soft-transitions';
import { UiHelperService } from '../../services/ui-helper.service';

export interface RseAuthModalData {
  authorizationUrl: string;
  processId: string;
  fileName?: string | null;
  /** Si true, abre Entrust al mostrar el modal (sin paso «Iniciar sesión»). */
  autoStart?: boolean;
  /** Empieza en «Preparando…» (create del proceso en curso). */
  preparing?: boolean;
  /** Popup ya abierto en el gesto del usuario (about:blank) para evitar bloqueo. */
  preopenedPopup?: Window | null;
}

export type RseAuthModalResult =
  | {
      status: 'completed';
      blob: Blob;
      process?: SignRseProcessResponse | null;
      signedFileName?: string | null;
    }
  | { status: 'cancel' }
  | { status: 'failed'; error: unknown };

export type RseAuthPanel =
  | 'idle'
  | 'preparing'
  | 'waiting'
  | 'signing'
  | 'success'
  | 'incomplete'
  | 'error';

/**
 * Flujo RSE continuo: preparing → waiting (Entrust) → signing → success.
 * Si el usuario cierra Entrust, se consulta el processId y se reacciona
 * sin pantallas en blanco ni reintentos sobre procesos ya terminales.
 */
@Component({
  selector: 'app-rse-auth-modal',
  templateUrl: './rse-auth-modal.component.html',
  styleUrls: ['./rse-auth-modal.component.css'],
  animations: [modalPanelAnimation],
  standalone: false
})
export class RseAuthModalComponent implements AfterViewInit, OnDestroy {
  fileName: string;
  authorizationUrl: string;
  processId: string;
  readonly autoStart: boolean;

  panel: RseAuthPanel = 'idle';
  mensajeError =
    'No pudimos completar la firma en este momento. Revisa que hayas iniciado sesión correctamente e inténtalo de nuevo.';

  private authPopup: Window | null = null;
  private popupWatchTimer: ReturnType<typeof setInterval> | null = null;
  private pollSub: Subscription | null = null;
  private statusProbeSub: Subscription | null = null;
  private closedBySuccess = false;
  private closedTerminal = false;
  private authStartTimer: ReturnType<typeof setTimeout> | null = null;
  private incompleteTimer: ReturnType<typeof setTimeout> | null = null;
  private autoStartDone = false;

  constructor(
    public dialogRef: MatDialogRef<RseAuthModalComponent, RseAuthModalResult>,
    @Inject(MAT_DIALOG_DATA) data: RseAuthModalData,
    private rseSignService: RseSignService,
    private http: HttpClient,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private uiHelper: UiHelperService
  ) {
    this.fileName = data?.fileName?.trim() || 'documento';
    this.authorizationUrl = data?.authorizationUrl?.trim() || '';
    this.processId = data?.processId?.trim() || '';
    this.autoStart = !!data?.autoStart;
    if (data?.preopenedPopup && !data.preopenedPopup.closed) {
      this.authPopup = data.preopenedPopup;
    }
    this.dialogRef.disableClose = true;
    if (data?.preparing) {
      this.panel = 'preparing';
    } else if (this.autoStart) {
      this.panel = 'waiting';
    }
    // En éxito el journey debe seguir visible: no unlockUi (mata el spinner).
    this.dialogRef.afterClosed().subscribe((result) => {
      if (result?.status !== 'completed') {
        this.uiHelper.unlockUi();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.autoStart && !this.autoStartDone && this.panel !== 'preparing') {
      this.autoStartDone = true;
      this.scheduleAuthStart(380);
    }
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.statusProbeSub?.unsubscribe();
    this.statusProbeSub = null;
    this.clearPopupWatch();
    this.clearIncompleteTimer();
    if (this.authStartTimer) {
      clearTimeout(this.authStartTimer);
      this.authStartTimer = null;
    }
    if (!this.closedBySuccess) {
      this.closeAuthWindow();
      this.uiHelper.unlockUi();
    }
  }

  /**
   * Tras create del proceso: pasa de preparing → waiting y abre Entrust.
   */
  beginExternalAuth(authorizationUrl: string, processId: string): void {
    this.authorizationUrl = (authorizationUrl || '').trim();
    this.processId = (processId || '').trim();
    this.autoStartDone = true;
    this.panel = 'waiting';
    this.cdr.markForCheck();
    this.scheduleAuthStart(420);
  }

  /**
   * Abrir / reabrir autenticación de forma segura según el estado real del proceso.
   */
  iniciarSesion(): void {
    this.clearIncompleteTimer();
    if (this.closedBySuccess || this.closedTerminal) {
      return;
    }

    if (!this.processId) {
      this.panel = 'waiting';
      this.cdr.markForCheck();
      this.openAuthWindow();
      this.maybeCompleteStubProcess();
      this.startWaitAndDownload();
      return;
    }

    this.panel = 'waiting';
    this.cdr.markForCheck();
    this.statusProbeSub?.unsubscribe();
    this.statusProbeSub = this.rseSignService.getProcess(this.processId).subscribe({
      next: (process) => this.resumeFromProcess(process, true),
      error: () => {
        // Sin estado: intentar reabrir auth si aún tenemos URL.
        this.panel = 'waiting';
        this.cdr.markForCheck();
        this.openAuthWindow();
        this.maybeCompleteStubProcess();
        this.startWaitAndDownload();
      }
    });
  }

  cancelar(): void {
    if (this.authStartTimer) {
      clearTimeout(this.authStartTimer);
      this.authStartTimer = null;
    }
    this.clearIncompleteTimer();
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.statusProbeSub?.unsubscribe();
    this.statusProbeSub = null;
    this.closeAuthWindow();
    this.uiHelper.hideSpinner();
    if (this.processId) {
      this.rseSignService.cancel(this.processId).subscribe({ error: () => {} });
    }
    this.dialogRef.close({ status: 'cancel' });
  }

  /** Cierre del panel de error (proceso ya terminal). */
  cerrarError(): void {
    if (this.closedTerminal) {
      return;
    }
    this.closeWithFailed(this.mensajeError);
  }

  private scheduleAuthStart(delayMs: number): void {
    if (this.authStartTimer) {
      clearTimeout(this.authStartTimer);
    }
    this.authStartTimer = setTimeout(() => {
      this.authStartTimer = null;
      this.iniciarSesion();
    }, delayMs);
  }

  /**
   * Reacciona al estado del processId sin retomar procesos muertos.
   * @param allowReopenAuth si true, puede volver a abrir Entrust (acción del usuario).
   */
  private resumeFromProcess(process: SignRseProcessResponse, allowReopenAuth: boolean): void {
    if (this.closedBySuccess || this.closedTerminal) {
      return;
    }

    const status = String(process.status || '').toUpperCase();
    const authUrl = (process.authorizationUrl || '').trim();
    if (authUrl) {
      this.authorizationUrl = authUrl;
    }

    if (status === 'COMPLETED') {
      this.beginDownloadAfterCompleted(process);
      return;
    }

    if (status === 'FAILED' || status === 'CANCELED') {
      this.handleTerminalFailure(status, process.errorMessage);
      return;
    }

    if (status === 'SIGNING') {
      this.clearIncompleteTimer();
      this.closeAuthWindow();
      this.panel = 'signing';
      this.cdr.markForCheck();
      this.startWaitAndDownload();
      return;
    }

    // PENDING_USER_AUTH / ACCEPTED / otros no terminales
    this.startWaitAndDownload();
    if (allowReopenAuth) {
      this.panel = 'waiting';
      this.cdr.markForCheck();
      this.openAuthWindow();
      this.maybeCompleteStubProcess();
      return;
    }
    // Popup cerrado y aún falta autenticar: retoma inmediata (sin pantalla en blanco).
    this.showIncomplete();
  }

  private openAuthWindow(): void {
    if (!this.authorizationUrl) {
      this.showIncomplete();
      return;
    }

    if (this.authPopup && !this.authPopup.closed) {
      try {
        this.authPopup.location.href = this.authorizationUrl;
      } catch {
        this.authPopup = null;
      }
      if (this.authPopup && !this.authPopup.closed) {
        this.authPopup.focus();
        this.watchPopup();
        return;
      }
    }

    const w = 900;
    const h = 650;
    const left = Math.max(0, Math.round((window.screen.width - w) / 2));
    const top = Math.max(0, Math.round((window.screen.height - h) / 2));
    const popup = window.open(
      this.authorizationUrl,
      'certitool_rse_auth',
      `popup=yes,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      this.showIncomplete();
      return;
    }

    this.authPopup = popup;
    popup.focus();
    this.watchPopup();
  }

  private watchPopup(): void {
    this.clearPopupWatch();
    this.ngZone.runOutsideAngular(() => {
      this.popupWatchTimer = setInterval(() => {
        let closed = false;
        try {
          closed = !this.authPopup || this.authPopup.closed;
        } catch {
          closed = true;
        }
        if (closed) {
          this.clearPopupWatch();
          this.authPopup = null;
          this.ngZone.run(() => this.onAuthPopupClosed());
        }
      }, 500);
    });
  }

  /**
   * Entrust suele cerrarse al autenticar mientras el proceso sigue en SIGNING.
   * Consultamos el processId de inmediato; si sigue pendiente, margen + incomplete.
   */
  private onAuthPopupClosed(): void {
    if (this.closedBySuccess || this.closedTerminal) {
      return;
    }
    if (this.panel === 'signing' || this.panel === 'success' || this.panel === 'error') {
      return;
    }
    if (!this.processId) {
      this.scheduleIncompleteIfStillWaiting();
      return;
    }

    this.statusProbeSub?.unsubscribe();
    this.statusProbeSub = this.rseSignService.getProcess(this.processId).subscribe({
      next: (process) => this.resumeFromProcess(process, false),
      error: () => this.scheduleIncompleteIfStillWaiting()
    });
  }

  private scheduleIncompleteIfStillWaiting(): void {
    this.clearIncompleteTimer();
    this.incompleteTimer = setTimeout(() => {
      this.incompleteTimer = null;
      if (this.closedBySuccess || this.closedTerminal) {
        return;
      }
      if (
        this.panel === 'waiting' ||
        this.panel === 'preparing' ||
        this.panel === 'incomplete'
      ) {
        // Última verificación antes de mostrar incomplete.
        if (!this.processId) {
          this.showIncomplete();
          return;
        }
        this.statusProbeSub?.unsubscribe();
        this.statusProbeSub = this.rseSignService.getProcess(this.processId).subscribe({
          next: (process) => {
            const status = String(process.status || '').toUpperCase();
            if (status === 'SIGNING' || status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELED') {
              this.resumeFromProcess(process, false);
              return;
            }
            this.showIncomplete();
          },
          error: () => this.showIncomplete()
        });
      }
    }, 8000);
  }

  private clearIncompleteTimer(): void {
    if (this.incompleteTimer) {
      clearTimeout(this.incompleteTimer);
      this.incompleteTimer = null;
    }
  }

  private showIncomplete(): void {
    // Mantener el poll: el proceso puede completar aunque el popup ya se cerró.
    this.panel = 'incomplete';
    this.cdr.detectChanges();
  }

  private startWaitAndDownload(): void {
    this.pollSub?.unsubscribe();
    if (!this.processId) {
      this.closeWithFailed(
        'No pudimos iniciar el proceso de firma. Inténtalo de nuevo.'
      );
      return;
    }

    this.pollSub = this.rseSignService.pollUntilTerminal(this.processId).subscribe({
      next: (process) => {
        const status = String(process.status || '').toUpperCase();
        if (status === 'FAILED' || status === 'CANCELED') {
          this.handleTerminalFailure(status, process.errorMessage);
          return;
        }
        if (status !== 'COMPLETED') {
          this.handleTerminalFailure(
            status,
            process.errorMessage || `El proceso de firma terminó en estado ${process.status}`
          );
          return;
        }
        this.beginDownloadAfterCompleted(process);
      },
      error: (error) => this.fail(error)
    });
  }

  private beginDownloadAfterCompleted(process: SignRseProcessResponse): void {
    this.clearIncompleteTimer();
    this.closeAuthWindow();
    this.uiHelper.showSignJourney({
      phase: 'prepare',
      fileName: this.fileName,
      mensaje: `Descargando y preparando la vista del documento (${this.fileName})…`
    });
    this.panel = 'signing';
    this.cdr.markForCheck();
    this.pollSub?.unsubscribe();
    this.pollSub = this.rseSignService.getResult(this.processId).subscribe({
      next: ({ blob, fileName }) =>
        this.handOffCompleted(blob, process, fileName),
      error: (error) => this.fail(error)
    });
  }

  private handleTerminalFailure(status: string, errorMessage?: string | null): void {
    this.clearIncompleteTimer();
    this.closeAuthWindow();
    this.uiHelper.hideSpinner();
    const message = this.messageForTerminalStatus(status, errorMessage);
    this.closeWithFailed(message);
  }

  private messageForTerminalStatus(status: string, errorMessage?: string | null): string {
    const detail = (errorMessage || '').trim();
    if (status === 'CANCELED') {
      return detail ||
        'El proceso de firma fue cancelado. Puedes intentarlo de nuevo cuando quieras.';
    }
    if (status === 'FAILED') {
      return detail ||
        'La firma no pudo completarse. Verifica tu autenticación e inténtalo de nuevo.';
    }
    return detail ||
      `El proceso de firma terminó en estado ${status || 'desconocido'}. Inténtalo de nuevo.`;
  }

  private closeWithFailed(message: string): void {
    if (this.closedTerminal || this.closedBySuccess) {
      return;
    }
    this.closedTerminal = true;
    this.mensajeError = message;
    this.panel = 'error';
    this.cdr.markForCheck();
    setTimeout(() => {
      this.dialogRef.close({
        status: 'failed',
        error: new Error(message)
      });
    }, 320);
  }

  private handOffCompleted(
    blob: Blob,
    process: SignRseProcessResponse,
    signedFileName: string | null
  ): void {
    this.clearIncompleteTimer();
    this.closedBySuccess = true;
    this.uiHelper.updateSignJourney({
      phase: 'prepare',
      fileName: signedFileName || this.fileName
    });
    this.dialogRef.close({
      status: 'completed',
      blob,
      process,
      signedFileName
    });
  }

  private fail(error: unknown): void {
    if (this.closedBySuccess || this.closedTerminal) {
      return;
    }
    // Timeout del poll con UI de reintento: dejar que el usuario elija.
    if (this.panel === 'incomplete') {
      return;
    }
    const err = error as { message?: string };
    const message =
      (err?.message && String(err.message).trim()) ||
      'No pudimos completar la firma en este momento. Revisa que hayas iniciado sesión correctamente e inténtalo de nuevo.';
    this.closeWithFailed(message);
  }

  private maybeCompleteStubProcess(): void {
    const isStubUrl =
      this.authorizationUrl.includes('rse.example.com') ||
      this.authorizationUrl.includes('stub-');
    if (!isStubUrl || !this.processId) {
      return;
    }
    const stubBase =
      (environment as { signRseStubBaseUrl?: string }).signRseStubBaseUrl ||
      'http://localhost:9095';
    setTimeout(() => {
      this.http
        .get(`${stubBase}/api/v1/sign/callbacks/rse`, {
          params: { localProcessId: this.processId, status: 'finished' }
        })
        .subscribe({ error: () => {} });
    }, 1500);
  }

  private clearPopupWatch(): void {
    if (this.popupWatchTimer) {
      clearInterval(this.popupWatchTimer);
      this.popupWatchTimer = null;
    }
  }

  private closeAuthWindow(): void {
    this.clearPopupWatch();
    try {
      if (this.authPopup && !this.authPopup.closed) {
        this.authPopup.close();
      }
    } catch {
      // ignore
    }
    this.authPopup = null;
  }
}
